/**
 * Shared pieces of the measured-overflow components (`Toolbar`, `Breadcrumb`): how they read a
 * container's usable width, how much rounding slack they allow, and how they keep a
 * `ResizeObserver` watching exactly the items currently in the container.
 */

/**
 * `clientWidth` is rounded to a whole pixel while element widths are fractional, so a row that
 * exactly fits can measure a fraction too wide. Without the slack it would collapse an item, free
 * the space, and expand again on the next pass.
 */
export const SUBPIXEL_TOLERANCE = 0.5;

/** A computed CSS length in pixels, or 0 when it is not a number (`normal`, an empty string). */
export function toPixels(value: string): number {
  const pixels = Number.parseFloat(value);
  return Number.isNaN(pixels) ? 0 : pixels;
}

/** The element's width available to its children: `clientWidth` without its inline padding. */
export function contentInlineSize(element: HTMLElement, style: CSSStyleDeclaration = getComputedStyle(element)): number {
  return element.clientWidth - toPixels(style.paddingInlineStart) - toPixels(style.paddingInlineEnd);
}

/**
 * Stops observing items that are no longer in `items`. A removed node left observed reports a 0
 * size mid-delivery, which would feed a stale measurement into the next decision.
 */
export function unobserveMissingItems(observer: ResizeObserver, items: readonly Element[], observed: Set<Element>): void {
  const present = new Set(items);

  for (const item of observed) {
    if (present.has(item)) continue;
    observer.unobserve(item);
    observed.delete(item);
  }
}

/** Observes the items now present and stops observing the ones that left. */
export function reconcileObservedItems(observer: ResizeObserver, items: readonly Element[], observed: Set<Element>): void {
  unobserveMissingItems(observer, items, observed);

  for (const item of items) {
    if (observed.has(item)) continue;
    observer.observe(item);
    observed.add(item);
  }
}
